import { TellescopeModelFromName, useUsersMap } from "@tellescope/tanstack-query";
import { cn } from "@tellescope/ui";
import { CSSProperties, useMemo } from "react";
import { Link } from "react-router-dom";
import { getEventCellColorsFromHex } from "../../utils";
import { addMinutes } from "date-fns/addMinutes";
import { isSameDay } from "date-fns/isSameDay";
import { Clock, TextAlignStart, User } from "lucide-react";
import { formatName } from "@tellescope/utils";
import { useDateFnsLocale } from "@tellescope/ui";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

export interface EventCardProps {
  event: TellescopeModelFromName<"calendar_events">;
  className?: string;
  onEventClick?: (() => void) | { to: string; replace?: boolean };
  timezone?: string;
}

export const EventCard = ({
  event,
  className,
  onEventClick,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: EventCardProps) => {
  const { t } = useTranslation("calendar");
  const Component = typeof onEventClick === "function" ? "button" : typeof onEventClick === "object" ? Link : "div";
  const locale = useDateFnsLocale();

  const { resolvedColor, opacity, strikethrough } = useMemo(() => {
    if (event.cancelledAt || event.rescheduledAt) {
      return { resolvedColor: "#000000", opacity: 0.5, strikethrough: true };
    }
    return { resolvedColor: event.color };
  }, [event.cancelledAt, event.color, event.rescheduledAt]);
  const { backgroundColor, borderColor, textColor } = getEventCellColorsFromHex(resolvedColor);
  const computedStyle: CSSProperties = {
    backgroundColor,
    borderColor,
    color: textColor,
    opacity,
  };
  const eventStartDate = useMemo(() => new Date(event.startTimeInMS), [event.startTimeInMS]);
  const eventEndDate = useMemo(
    () => addMinutes(eventStartDate, event.actualDuration ?? event.durationInMinutes),
    [event.actualDuration, event.durationInMinutes, eventStartDate]
  );
  const isMultiDay = useMemo(() => !isSameDay(eventEndDate, eventStartDate), [eventEndDate, eventStartDate]);

  const usersMap = useUsersMap();
  const userNames = useMemo(
    () =>
      event.attendees
        .reduce<string[]>((acc, curr) => {
          if (curr.type === "user") {
            const user = usersMap[curr.id];
            if (user) {
              acc.push(formatName(user));
            }
          }

          return acc;
        }, [])
        .join(", "),
    [event.attendees, usersMap]
  );

  return (
    <Component
      to={typeof onEventClick === "object" && typeof Component !== "string" ? onEventClick.to : ""}
      replace={typeof onEventClick === "object" && typeof Component !== "string" ? onEventClick.replace : undefined}
      onClick={typeof onEventClick === "function" && Component === "button" ? onEventClick : undefined}
      className={cn(
        "border rounded-sm p-3 text-xs flex flex-col overflow-hidden font-light gap-2.5",
        strikethrough && "line-through",
        className
      )}
      style={computedStyle}
    >
      <div className="font-medium text-sm">{event.displayTitle || event.title}</div>
      <div className="flex flex-col gap-2">
        <span className="flex items-center gap-1">
          <User size={12} />
          <span className="text-primary">{userNames}</span>
        </span>
        <span className="flex items-center gap-1">
          <Clock size={12} />
          <span className="text-primary">
            {t("{{startTime}} - {{endTime}}", {
              startTime: formatInTimeZone(eventStartDate, timezone, "h:mm a", { locale }),
              endTime: formatInTimeZone(eventEndDate, timezone, isMultiDay ? "h:mm a (MMM d, yyyy)" : "h:mm a", {
                locale,
              }),
            })}
          </span>
        </span>
        {!!event.description && (
          <span className="flex items-center gap-1">
            <TextAlignStart size={12} />
            <span className="text-primary">{event.description}</span>
          </span>
        )}
      </div>
    </Component>
  );
};
